import React from 'react'
import { Group, Select, Stack, Text, TextInput } from '@mantine/core'

import {
  CrTurnaroundSelection,
  CrTurnaroundValue,
  EXPEDITED_PENDING,
  EXPEDITED_TURNAROUND_OPTIONS,
  STANDARD_TURNAROUND_VALUE,
} from '@/constants'

type Speed = 'none' | 'standard' | 'expedited'

// Speed is derived from the stored value: the pending-expedited sentinel keeps "Expedited,
// day not yet picked" distinct from "Not ordered" (null), so it survives reload too.
const deriveSpeed = (value: CrTurnaroundSelection): Speed => {
  if (!value) {
    return 'none'
  }
  return value === STANDARD_TURNAROUND_VALUE ? 'standard' : 'expedited'
}

// Per-attendee transcript-order turnaround for the staged CR form: a Standard/Expedited
// dropdown plus a day dropdown. Standard is fixed at 10 days; Expedited lets the SA pick a
// specific day. Stored via onChange (Standard = 240, Expedited = the picked value or the
// pending sentinel until a day is chosen, Not ordered = null); the payload builder maps
// the real values to a backend-supported turnaround.
export const CrTurnaroundSelect: React.FC<{
  label: string
  value: CrTurnaroundSelection
  onChange: (value: CrTurnaroundSelection) => void
}> = ({ label, value, onChange }) => {
  const speed = deriveSpeed(value)

  return (
    <Stack spacing="xs">
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Group grow align="flex-start" noWrap>
        <Select
          label="Order"
          placeholder="Select one"
          data={[
            { value: 'none', label: 'Not ordered' },
            { value: 'standard', label: 'Standard' },
            { value: 'expedited', label: 'Expedited' },
          ]}
          value={speed}
          onChange={next => {
            if (next === 'standard') {
              onChange(STANDARD_TURNAROUND_VALUE)
            } else if (next === 'expedited') {
              // Hold the pending sentinel until a day is picked so submit can be blocked.
              onChange(EXPEDITED_PENDING)
            } else {
              onChange(null)
            }
          }}
        />
        {speed === 'standard' && (
          <TextInput label="Turnaround" value="10 Days" readOnly />
        )}
        {speed === 'expedited' && (
          <Select
            withAsterisk
            label="Turnaround"
            placeholder="Select turnaround"
            data={EXPEDITED_TURNAROUND_OPTIONS}
            value={value === EXPEDITED_PENDING ? null : value}
            error={
              value === EXPEDITED_PENDING ? 'Select a turnaround' : undefined
            }
            onChange={next => onChange(next as CrTurnaroundValue | null)}
          />
        )}
      </Group>
    </Stack>
  )
}
